import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Collapse,
  TablePagination,
  CircularProgress,
  Typography
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Brightness1 as Brightness1Icon
} from "@mui/icons-material";
import { IDatagridSimple, TCollapse, TDatagridData, TOrder } from "../types";
import { DgTilte, Lbl, RulesContainer, RulesText } from "./styles";
import { useNavigationContext } from "../../../contexts";
import { SeeMore } from "../components/see-more";
import { ModalGeneric } from "../../modal-template/modal-generic";
import theme from "../../../styles/theme";
import { ContentConditional } from "@/components/content-conditional";
import { ModalDelete } from "@/components/modal-template/modal-delete";
import {
  formatDateTime,
  formatText,
  getComparator,
  renderDelete,
  renderDownload,
  renderEdit
} from "../utils/datagrid";

/* ===== Componente Principal ===== */
export const MobileDatagrid: React.FC<IDatagridSimple> = ({
  navigateTable,
  handleDelete,
  handleEdit,
  checkedRows,
  handleDownload,
  headers,
  data,
  count,
  hasCheckbox,
  rowHeader,
  orderBy: orderByParam,
  loading,
  page,
  renderExpandedRow
}) => {
  /* ===== Estados e Contextos ===== */
  const { seeMoreText, seeMoreModalWidth, rowsPerPage, setRowsPerPage } =
    useNavigationContext();
  const [selected, setSelected] = useState<number[]>([]);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<TCollapse[]>([]);
  const [seeMoreVisible, setSeeMoreVisible] = useState<boolean>(false);

  // Ordem fixa conforme código original
  const [order] = useState<TOrder>("desc");
  const [orderBy] = useState<string>(orderByParam ?? "");
  const [modalDelete, setModalDelete] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);

  /* ===== Funções Auxiliares ===== */
  const toggleModalSeeMore = () => setSeeMoreVisible((prev) => !prev);
  const toggleModalDelete = () => setModalDelete((prevState) => !prevState);

  /* ===== Paginação e Ordenação ===== */
  const visibleRows = useMemo(() => {
    return [...data].sort(getComparator(order, orderBy)).slice(0, rowsPerPage);
  }, [page, data, rowsPerPage, order, orderBy]);

  /* ===== Inicialização do Collapse ===== */
  useEffect(() => {
    const initialCollapse = data.map((_, idx) => ({
      id: idx.toString(),
      collapsed: false
    }));
    setCollapsed(initialCollapse);
  }, [data]);

  /* ===== Seleção de Linhas ===== */
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelectedIds = visibleRows.map((row) => row.id);
      const uniqueSelected = Array.from(
        new Set([...selected, ...newSelectedIds])
      );
      setSelected(uniqueSelected);
      checkedRows?.(uniqueSelected);
      setIsChecked(true);
    } else {
      const deselectedIds = visibleRows.map((row) => row.id);
      const updatedSelected = selected.filter(
        (id) => !deselectedIds.includes(id)
      );
      setSelected(updatedSelected);
      checkedRows?.(updatedSelected);
      setIsChecked(false);
    }
  };

  /* ===== Manipulação do Collapse ===== */
  const handleCollapseToggle = (id: string) => {
    setCollapsed((prev) =>
      prev.map((col) =>
        col.id === id ? { ...col, collapsed: !col.collapsed } : col
      )
    );
  };

  /* ===== Renderizadores ===== */
  const renderCell = (field: string, title: string, cellData: unknown) => {
    if (field === "seeMore" || field === "seeMore1") {
      return SeeMore({ text: cellData as string, toggle: toggleModalSeeMore });
    }

    if (field !== "id") {
      let value = cellData;
      if (typeof value === "string") {
        value = formatDateTime(value);
      }
      return (
        <TableCell key={Math.random()} sx={{ padding: "10px" }}>
          <DgTilte>{title}: </DgTilte>
          {value as string}
        </TableCell>
      );
    }
    return null;
  };

  const renderRowCells = (row: TDatagridData) => {
    const rowKeys = Object.keys(row);
    return headers.map((header, index) =>
      renderCell(rowKeys[index], header.field, row[rowKeys[index]])
    );
  };

  const renderTools = (row: TDatagridData) => {
    const indexParam = headers.findIndex((h) => h.field === "Ações");
    if (indexParam + 1 === headers.length) {
      return (
        <TableCell key={Math.random()}>
          <Box display="flex" alignItems="center">
            <DgTilte>Ações</DgTilte>
            <Box display="flex" gap={2} marginLeft="auto">
              {handleEdit && renderEdit(row, handleEdit)}
              {handleDelete &&
                renderDelete(row, setItemToDelete, toggleModalDelete)}
              {handleDownload && renderDownload(row, handleDownload)}
            </Box>
          </Box>
        </TableCell>
      );
    }
    return null;
  };

  const renderCollapseRow = (row: TDatagridData, index: number) => (
    <>
      <TableRow>
        <TableCell
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "7px",
            color: "black",
            backgroundColor:
              index % 2 !== 0
                ? theme.colors.palete.tableOdd
                : theme.colors.palete.brownExtraLighter
          }}
        >
          {row[rowHeader!]}
          <IconButton
            sx={{ marginLeft: "auto" }}
            aria-label="expand row"
            size="small"
            onClick={() => handleCollapseToggle(index.toString())}
          >
            {collapsed[index]?.collapsed ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
          {checkCellHeaderMobile(row[headers[0].field])}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={headers.length + (hasCheckbox ? 1 : 0)}
          sx={{ padding: 0, borderBottom: 0 }}
        >
          <Collapse
            in={collapsed[index]?.collapsed}
            timeout="auto"
            unmountOnExit
          >
            <Box margin={1} display="flex" flexDirection="column" gap={1}>
              {renderRowCells(row)}
              {renderTools(row)}
              {renderExpandedRow && <Box mt={2}>{renderExpandedRow(row)}</Box>}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );

  /* ===== Renderizador para Cabeçalho de Célula Mobile ===== */
  const checkCellHeaderMobile = (val: string | object) => {
    if (typeof val === "string" && val.startsWith("#") && val[7] === "-") {
      const color = val.substring(0, 7);
      const label = val.substring(8);
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={label} arrow placement="top">
            <Brightness1Icon sx={{ fill: color }} />
          </Tooltip>
          {label}
        </Box>
      );
    }
    if (typeof val === "object") return null;
    return <Lbl>{val}</Lbl>;
  };

  /* ===== Renderização do JSX ===== */
  return (
    <>
      <ModalGeneric
        isOpen={seeMoreVisible}
        toggle={toggleModalSeeMore}
        title="Regra:"
        maxWidth={seeMoreModalWidth}
        maxHeight="50vh"
      >
        <RulesContainer style={{ maxHeight: "50vh" }}>
          <RulesText>{formatText(seeMoreText)}</RulesText>
        </RulesContainer>
      </ModalGeneric>
      <ContentConditional.IsShow isShow={!!handleDelete}>
        <ModalDelete
          isOpen={modalDelete}
          title={handleDelete?.modalTitle ?? ""}
          action={async () => {
            handleDelete!.onDelete(itemToDelete);
            setItemToDelete(null);
            toggleModalDelete();
          }}
          description={handleDelete?.modalText ?? ""}
          toggle={toggleModalDelete}
          isDisabledButton={false}
        />
      </ContentConditional.IsShow>
      <TableContainer>
        <Table sx={{ backgroundColor: theme.colors.palete.tableOdd }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="200px"
              sx={{ backgroundColor: theme.colors.palete.brownExtraLighter }}
            >
              <CircularProgress color="inherit" />
            </Box>
          ) : visibleRows && visibleRows.length > 0 ? (
            <>
              <TableHead>
                <TableRow
                  sx={{ borderBottom: "1px solid rgba(224, 224, 224, 1)" }}
                >
                  {hasCheckbox && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isChecked}
                        onChange={handleSelectAllClick}
                        inputProps={{ "aria-label": "selecionar todos" }}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    key={0}
                    sx={{
                      width: headers[0].width || "auto",
                      fontWeight: "800"
                    }}
                  >
                    {headers[0].field}:
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    {renderCollapseRow(row, index)}
                  </React.Fragment>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 50, 100]}
                    labelRowsPerPage="Linhas"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page - 1}
                    onPageChange={(_, newPage) =>
                      navigateTable((newPage + 1).toString())
                    }
                    onRowsPerPageChange={(event) =>
                      setRowsPerPage(parseInt(event.target.value))
                    }
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from} - ${to} de ${
                        count !== -1 ? count : `mais de ${to}`
                      }`
                    }
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: theme.colors.palete.brownDark
                      },
                      "& .Mui-selected": {
                        backgroundColor: "white !important"
                      }
                    }}
                  />
                </TableRow>
              </TableFooter>
            </>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="200px"
              sx={{ backgroundColor: theme.colors.palete.brownExtraLighter }}
            >
              <Typography
                variant="h6"
                color="textSecondary"
                sx={{ fontFamily: "sans-serif", fontWeight: "400" }}
              >
                Nenhum registro encontrado.
              </Typography>
            </Box>
          )}
        </Table>
      </TableContainer>
    </>
  );
};
